import { useState } from "react";
import { Link } from "react-router";
import { ChevronRight, Gift, MinusCircle, Package, PlusCircle, Repeat, UserPlus } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { SwitchToKidSheet } from "@/components/SwitchUser";
import { ConsentCard } from "./Privacy";
import { Badge, Button, Card, EmptyState, IconButton, PageLoader, SectionTitle } from "@/components/ui";
import { Avatar, Points, ProgressRing } from "@/components/bits";
import { LEDGER_LABELS, timeAgo } from "@/lib/format";
import { useActivity, useApprovals, useKids, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { formatDuration } from "@shared/duration";
import type { KidSummaryDTO } from "@shared/types";
import { BonusSheet, DeductSheet } from "./actions";

function KidCard({ s, onBonus, onDeduct }: { s: KidSummaryDTO; onBonus: () => void; onDeduct: () => void }) {
  const { family } = useSession();
  const rewards = useRewardTypes();
  const time = s.banks[0];
  const timeType = rewards.data?.rewardTypes.find((t) => t.id === time?.rewardTypeId);
  return (
    <Card className="p-0">
      <Link to={`/parent/kids/${s.kid.id}`} className="flex items-center gap-3 p-4">
        <Avatar member={s.kid} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="text-lg font-extrabold">{s.kid.name}</div>
          <Points value={s.balance} family={family} className={s.balance < 0 ? "text-xl text-danger" : "text-xl text-coin-ink"} />
          <div className="mt-0.5 flex flex-wrap gap-1">
            {s.pendingJobRuns > 0 && <Badge tone="warn">{s.pendingJobRuns} to check</Badge>}
            {time?.session && timeType && <Badge tone="coin">{timeType.icon} Screen time running</Badge>}
            {time && timeType && time.balance > 0 && (
              <Badge>
                {timeType.icon} {formatDuration(time.balance)} saved
              </Badge>
            )}
            {s.earnedThisWeek > 0 && <Badge tone="brand">+{s.earnedThisWeek} this week</Badge>}
          </div>
        </div>
        <div className="flex flex-col items-center">
          <ProgressRing value={s.gate.total ? s.gate.done / s.gate.total : 1} size={52} stroke={6}>
            <span className="text-xs font-black">{s.gate.total ? `${s.gate.done}/${s.gate.total}` : "✓"}</span>
          </ProgressRing>
          <span className="mt-1 text-xs font-bold text-muted">{s.gate.unlocked ? "Unlocked" : "Duties"}</span>
        </div>
      </Link>
      <div className="grid grid-cols-2 border-t border-line">
        <button type="button" onClick={onBonus} className="flex items-center justify-center gap-1.5 py-3 text-sm font-bold text-coin-ink hover:bg-sunken">
          <PlusCircle className="size-4" /> Bonus
        </button>
        <button type="button" onClick={onDeduct} className="flex items-center justify-center gap-1.5 border-l border-line py-3 text-sm font-bold text-danger hover:bg-sunken">
          <MinusCircle className="size-4" /> Deduct
        </button>
      </div>
    </Card>
  );
}

export function ParentHome() {
  const { user, family, memberById, kids: members } = useSession();
  const kids = useKids();
  const approvals = useApprovals();
  const activity = useActivity();
  const [sheet, setSheet] = useState<{ kind: "bonus" | "deduct"; summary: KidSummaryDTO } | null>(null);
  const [switching, setSwitching] = useState(false);

  if (kids.isLoading || !kids.data) return <PageLoader />;
  const a = approvals.data;

  return (
    <div>
      <PageHeader
        title={`Hi ${user.name}`}
        subtitle={family.name}
        bell="/parent/notifications"
        actions={
          members.length > 0 && (
            <IconButton label="Switch to a kid" onClick={() => setSwitching(true)}>
              <Repeat className="size-6" />
            </IconButton>
          )
        }
      />
      <SwitchToKidSheet open={switching} onClose={() => setSwitching(false)} />
      <div className="mt-3">
        <ConsentCard compact />
      </div>

      {a && a.count > 0 && (
        <Link to="/parent/approvals">
          <Card className="mt-3 flex items-center gap-3 bg-coin-soft ring-coin/40 transition hover:brightness-[0.98]">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-coin text-lg font-black text-white">{a.count}</span>
            <div className="min-w-0 flex-1">
              <div className="text-lg font-extrabold text-coin-ink">Waiting on you</div>
              <div className="truncate text-sm font-semibold text-coin-ink/80">
                {[
                  a.jobRuns.length && `${a.jobRuns.length} finished job${a.jobRuns.length === 1 ? "" : "s"}`,
                  a.pitches.length && `${a.pitches.length} pitch${a.pitches.length === 1 ? "" : "es"}`,
                  a.redemptions.length && `${a.redemptions.length} spend request${a.redemptions.length === 1 ? "" : "s"}`,
                  a.completions.length && `${a.completions.length} check-off${a.completions.length === 1 ? "" : "s"}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <ChevronRight className="size-5 text-coin-ink" />
          </Card>
        </Link>
      )}

      <SectionTitle
        action={
          <Link to="/parent/family" className="flex items-center gap-1 text-sm font-bold text-brand">
            <UserPlus className="size-4" /> Manage
          </Link>
        }
      >
        Kids
      </SectionTitle>
      {kids.data.kids.length === 0 ? (
        <EmptyState
          icon="👧"
          title="Add your kids"
          action={
            <Link to="/parent/family">
              <Button>Add a kid</Button>
            </Link>
          }
        >
          Each kid gets their own PIN login, balance, and rewards.
        </EmptyState>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {kids.data.kids.map((s) => (
            <KidCard key={s.kid.id} s={s} onBonus={() => setSheet({ kind: "bonus", summary: s })} onDeduct={() => setSheet({ kind: "deduct", summary: s })} />
          ))}
        </div>
      )}

      {a && a.toDeliver.length > 0 && (
        <>
          <SectionTitle>To hand over</SectionTitle>
          <Card className="divide-y divide-line p-0">
            {a.toDeliver.slice(0, 5).map((r) => (
              <Link key={r.id} to="/parent/approvals" className="flex items-center gap-3 px-4 py-3 hover:bg-sunken">
                <Package className="size-5 text-coin" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-bold">{r.title}</div>
                  <div className="text-xs text-muted">for {memberById(r.kidId)?.name}</div>
                </div>
                <ChevronRight className="size-4 text-faint" />
              </Link>
            ))}
          </Card>
        </>
      )}

      <SectionTitle>Recent activity</SectionTitle>
      {!activity.data || activity.data.entries.length === 0 ? (
        <EmptyState icon={<Gift className="mx-auto size-8 text-faint" />} title="Nothing yet">
          Approved jobs, bonuses, spending and consequences show up here.
        </EmptyState>
      ) : (
        <Card className="divide-y divide-line p-0">
          {activity.data.entries.slice(0, 12).map((e) => {
            const kid = memberById(e.kidId);
            return (
              <div key={e.id} className="flex items-center gap-3 px-4 py-3">
                {kid && <Avatar member={kid} size="sm" />}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold">{e.reason || LEDGER_LABELS[e.kind]}</div>
                  <div className="truncate text-xs text-muted">
                    {kid?.name} · {LEDGER_LABELS[e.kind]} · {timeAgo(e.createdAt)}
                  </div>
                </div>
                <Points value={e.amount} family={family} signed className={e.amount >= 0 ? "text-brand" : "text-danger"} />
              </div>
            );
          })}
        </Card>
      )}

      {sheet?.kind === "bonus" && <BonusSheet summary={sheet.summary} onClose={() => setSheet(null)} />}
      {sheet?.kind === "deduct" && <DeductSheet summary={sheet.summary} onClose={() => setSheet(null)} />}
    </div>
  );
}
